import React from "react";
import { NavLogo } from "../Navigation";
import DownloadApp from "./DownloadApp";
import { AboutLinks, LegalLinks } from "sections/Account/Links";
import FooterLink from "./FooterLink";
import { ReactComponent as Logo } from "assets/logo.svg";

import { useIsBettingSection, useDisplayAtMost } from "hooks";
import { useIsClient } from "usehooks-ts";

import * as styles from "./Footer.module.scss";
import ResponsibleGambling from "../ResponsibleGambling";
import { SACodeOfPractice } from "./SACodeOfPractice";
import { useCustomerLicence } from "hooks/firestore/v2/user/useProfile";
import {
  type CustomerLicence,
  CustomerLicenceOptions,
} from "utilities/Auth/authSlice";
import SocialIcons from "./SocialIcons";
import SkeletonParagraph from "components/Skeleton/SkeletonParagraph";
import IsleOfManFooterContent from "./IsleOfManFooterContent";

const InfoContent = ({ licence }: { licence: CustomerLicence | null }) => {
  const isIsleOfMan = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  if (!licence) {
    return <SkeletonParagraph />;
  }

  if (isIsleOfMan) {
    return <IsleOfManFooterContent />;
  }

  return (
    <div className={styles.section}>
      <p>
        Licensed and regulated by the Northern Territory Racing and Wagering
        Commission.
      </p>
      <p className={styles.showMinWidthDesktop}>
        <SACodeOfPractice />
      </p>
      <p>
        Puntaa Pty Ltd trading as Picklebet. Unit 1, 48-50 Smith Street Darwin
        NT 0800.
      </p>
      <ResponsibleGambling />
    </div>
  );
};

const InfoContentCompact = ({
  licence,
}: { licence: CustomerLicence | null }) => {
  const isIsleOfMan = licence === CustomerLicenceOptions.ISLE_OF_MAN;

  if (!licence) {
    return (
      <div className={styles.compactFooterSection}>
        <SkeletonParagraph />
      </div>
    );
  }

  if (isIsleOfMan) {
    return (
      <div className={styles.compactFooterSection}>
        <IsleOfManFooterContent />
      </div>
    );
  }

  return null;
};

const Footer = () => {
  const isTabletAndBelow = useDisplayAtMost("tablet");
  const isClient = useIsClient();
  const isBettingSection = useIsBettingSection();
  const licence = useCustomerLicence();
  const isNTRWC = licence === CustomerLicenceOptions.NTRWC;

  if (!isClient || (isTabletAndBelow && !isBettingSection)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.expanded}>
        <div className={styles.section}>
          <NavLogo />
          <DownloadApp />
        </div>
        <div className={styles.section}>
          <h3>Info</h3>
          <InfoContent licence={licence} />
        </div>
        <div className={styles.section}>
          <h3>Legal</h3>
          {LegalLinks.map((link) => (
            <FooterLink key={link.name} link={link} />
          ))}
        </div>
        <div className={styles.section}>
          <h3>Help</h3>
          {AboutLinks.map((link) => (
            <FooterLink key={link.name} link={link} />
          ))}
        </div>
      </div>
      <hr />
      <div className={styles.compact}>
        <div className={styles.logo}>
          <Logo />
        </div>
        <SocialIcons />
        <div className={styles.compactInfoContent}>
          {isNTRWC && (
            <div className={styles.links}>
              <FooterLink
                link={{
                  name: "Terms & Conditions",
                  supportOptions: { labels: ["weblink-terms-and-conditions"] },
                }}
              />
              <FooterLink
                link={{
                  name: "Responsible Gambling",
                  supportOptions: {
                    labels: ["weblink-responsible-gambling-policy"],
                  },
                }}
              />
            </div>
          )}
          {isNTRWC && (
            <div className={styles.sacop}>
              <SACodeOfPractice />
            </div>
          )}
        </div>
      </div>

      {isTabletAndBelow && isNTRWC && <ResponsibleGambling />}

      {!isNTRWC && (
        <div className={styles.mobileTextContent}>
          <InfoContentCompact licence={licence} />
        </div>
      )}
    </div>
  );
};

export default Footer;
